import type { FormEvent, ReactNode } from "react";
import Modal from "@/components/Modal/Modal";
import Button from "@/components/Button/Button";
import CrossIcon from "@/components/SVGComponents/CrossIcon";
import { combineClasses } from "@/lib/utils";
import styles from "./FormModal.module.scss";

export interface FormModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  submitLabel: string;
  submitIcon?: ReactNode;
  cancelLabel?: string;
  submitDisabled?: boolean;
  // Widens/narrows the panel for content that needs it (e.g. a 2-column
  // field grid) — merged with the base modal layout, not a replacement.
  className?: string | undefined;
  // Extra class for the Cancel/Submit buttons (e.g. a compact size).
  footerButtonClassName?: string | undefined;
  children: ReactNode;
}

// Shared chrome for every "modal with a form in it" — title + close
// button, an optional subtitle, a content slot, and a Cancel/Submit
// footer with a dynamic submit label and icon. Extracted after the same
// header/subtitle/footer markup showed up nearly identically in
// `ExportModal` and `AddMemberModal` (see docs/decisions.md #25).
const FormModal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  onSubmit,
  submitLabel,
  submitIcon,
  cancelLabel = "Cancel",
  submitDisabled = false,
  className,
  footerButtonClassName,
  children,
}: FormModalProps) => (
  <Modal isOpen={isOpen} onClose={onClose} className={combineClasses(styles.formModal, className)}>
    <div className={styles.header}>
      <h2 className={styles.title}>{title}</h2>
      <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close">
        <CrossIcon withBg={false} height={13} width={13} stroke="currentColor" strokeWidth={2} />
      </button>
    </div>
    {subtitle && <p className={styles.subtitle}>{subtitle}</p>}

    <form onSubmit={onSubmit} className={styles.body}>
      <div className={styles.content}>{children}</div>

      <div className={styles.footer}>
        <Button
          outlined
          type="button"
          onClick={onClose}
          className={combineClasses(styles.footerButton, footerButtonClassName)}
        >
          {cancelLabel}
        </Button>
        <Button
          type="submit"
          disabled={submitDisabled}
          className={combineClasses(styles.footerButton, footerButtonClassName)}
        >
          {submitIcon && <span className={styles.submitIcon}>{submitIcon}</span>}
          {submitLabel}
        </Button>
      </div>
    </form>
  </Modal>
);

export default FormModal;
